import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../Sidebar";
import { getPopoverByName } from "../../../../utils";
import { selectSubTab } from "../../../../store/common/uiSlice";
import { setCurrentCollection } from "../../../../store/templates/templatesSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowAltCircleLeft,
  faArrowAltCircleRight,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../Loader";
import TemplateDropdownMenu from "../../../templates/TemplateMenu";
import { useNavigate, useParams } from "react-router-dom";
import { InfiniteScroll } from "../../infiniteScroller";
import { PAGINATION_OFFSET } from "../../../../utils/general";
import { debounce } from "../../../../utils/debounce";
import { generateNewIdAndNavigate } from "../../../../thunks/dashboardThunks";
import { DEFAULT_PROJECT_NAMES } from "../../../../constants/general";
import { useCanvasContext } from "../../../editor/CanvasContext";
import { TEMPLATE_LAYOUT } from "../../../../constants/dashboard";

const TemplatePopover = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const thisPopover = getPopoverByName("Themes");
  const { canvas } = useCanvasContext();
  const [openTemplateMenuIndex, setOpenTemplateMenuIndex] = useState();
  const [search, setSearch] = useState("");
  const { id } = useParams();

  // // Selectors to access templates and template categories from Redux state
  const {
    templates: groupedTemplate,
    userTemplates,
    totalTemplatesCount,
    totalUserTemplatesCount,
    collections,
    currentCollection,
    featureTemplateLoading,
    userTemplateLoading,
  } = useSelector((state) => state.templates);
  const loading = useMemo(
    () => userTemplateLoading || featureTemplateLoading,
    [featureTemplateLoading, userTemplateLoading]
  );

  const subTab = useSelector((state) => state.ui.subTab);
  const selectedTab = useMemo(() => thisPopover.tabs[subTab], [subTab]);
  const isMyTemplateSelection = useMemo(
    () => selectedTab.name === "My Themes",
    [selectedTab]
  );
  const formattedCollections = useMemo(
    () =>
      isMyTemplateSelection ? [] : [{ name: "All", id: 0 }, ...collections],
    [collections, isMyTemplateSelection]
  );

  const onTabSelect = useCallback(
    (tabIndex) => {
      dispatch(selectSubTab(tabIndex));
    },
    [dispatch]
  );

  const fetchData = (page, search) =>
    dispatch(selectedTab.collections(page, search, 20));

  useEffect(() => {
    fetchData(0);
  }, [subTab]);

  const { filteredTemplates, totalCount } = useMemo(() => {
    return {
      filteredTemplates: isMyTemplateSelection
        ? userTemplates
        : groupedTemplate,
      totalCount: isMyTemplateSelection
        ? totalUserTemplatesCount
        : totalTemplatesCount,
    };
  }, [
    isMyTemplateSelection,
    userTemplates,
    groupedTemplate,
    totalTemplatesCount,
    totalUserTemplatesCount,
  ]);

  const hasNext = useMemo(() => {
    return filteredTemplates.length !== totalCount;
  }, [filteredTemplates, totalCount]);

  const handleNext = () => {
    const currentPage = Math.ceil(filteredTemplates.length / PAGINATION_OFFSET);
    const totalPages = Math.ceil(totalCount / PAGINATION_OFFSET);
    if (totalPages > currentPage) {
      return fetchData(currentPage, search);
    }
  };

  const handleSearch = debounce((value) => {
    setSearch(value);
    return fetchData(0, value);
  }, 800);

  const handleSlideMenu = (index) => {
    return setOpenTemplateMenuIndex((prev) =>
      index !== undefined ? (prev === index ? undefined : index) : undefined
    );
  };

  const scrollContainer = useRef(null);

  const scrollLeft = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: 150, behavior: "smooth" });
    }
  };

  const handleTemplateSelection = (item) => {
    canvas.clear();
    if (!isMyTemplateSelection) {
      return dispatch(
        generateNewIdAndNavigate(
          navigate,
          "/tool/editor/" + "user-template" + "/",
          DEFAULT_PROJECT_NAMES["user-template"],
          false,
          item.id,
          canvas,
          id
        )
      );
    }
    return navigate(`/tool/editor/user-template/${item.id}`);
  };

  const onCollectionSelect = (collectionId) => {
    dispatch(setCurrentCollection(collectionId));
    const name = collections.find((c) => c.id === collectionId)?.name;
    return handleSearch(name || "");
  };

  return (
    <div
      className="flex w-full h-full"
      onClick={(e) => {
        e.preventDefault();
        handleSlideMenu();
      }}
    >
      <Sidebar
        titleIcon={"/tool/dashboard/static/images/icons/tools/templates.svg"}
        title={"Themes"}
        tabs={thisPopover.tabs}
        onTabSelect={onTabSelect}
        isDisabled={loading}
      />
      <div className="py-2 px-1 w-4/5 bg-gray-100 overflow-visible">
        <div className="mb-2 flex justify-between items-center">
          <input
            type="text"
            placeholder={`Search ${selectedTab.name}`}
            className="w-full p-2 border rounded-lg"
            onChange={(e) => {
              if (e.target.value) setSearch(e.target.value);
            }}
            disabled={loading}
            onBlur={(e) => {
              handleSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch(e.target.value);
              }
            }}
          />
        </div>
        <div className="relative w-full h-full">
          {formattedCollections && !!formattedCollections.length && (
            <div className="mb-3 flex items-center">
              <span
                className="text-gray-400 cursor-pointer mr-2"
                onClick={scrollLeft}
              >
                <FontAwesomeIcon
                  icon={faArrowAltCircleLeft}
                  className="mr-2 text-gray-800 hover:text-vb-pink"
                />
              </span>
              <div
                ref={scrollContainer}
                className="flex overflow-x-auto scroll-container space-x-4 items-center"
              >
                {formattedCollections.map(({ name, id }, index) => (
                  <button
                    key={name + id}
                    className={`${
                      "whitespace-nowrap min-w-min px-2 py-1 text-xs text-vb-label-gray2 rounded-full hover:pointer " +
                      (currentCollection === id ? "bg-gray-200" : "")
                    }`}
                    onClick={() => !loading && onCollectionSelect(id, index)}
                  >
                    {name}
                  </button>
                ))}
              </div>

              <span
                className="text-gray-400 cursor-pointer ml-2 "
                onClick={scrollRight}
              >
                <FontAwesomeIcon
                  icon={faArrowAltCircleRight}
                  className="mr-2 text-gray-800 hover:text-vb-pink"
                />
              </span>
            </div>
          )}

          {!loading && !filteredTemplates.length ? (
            <div className="flex flex-grow justify-center text-sm text-gray-500 items-center text-center w-full">
              {" "}
              No images to show.{" "}
            </div>
          ) : (
            <div className="w-full overflow-y-auto overflow-x-visible">
              {!!filteredTemplates.length && (
                <InfiniteScroll
                  fetchMore={handleNext}
                  totalItems={filteredTemplates.length}
                  hasMore={hasNext}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    flexWrap: "wrap",
                  }}
                  className="gap-4"
                  height={!formattedCollections.length ? 320 : 300}
                  hideLoader
                >
                  {filteredTemplates.map((template, index) => (
                    <div className="w-full">
                      <div
                        className=" aspect-[114/98] w-full rounded flex justify-center items-center cursor-pointer relative"
                        onClick={(e) => {
                          e.preventDefault();
                          return handleTemplateSelection(template);
                        }}
                      >
                        {template.isFavorite && (
                          <img
                            className="cursor-pointer w-4 h-4 bg-gradient p-[2px] rounded-[50%]  absolute top-2 left-2 text-white z-50"
                            src="/tool/dashboard/static/images/icons/tools/like.svg"
                          />
                        )}
                        <FontAwesomeIcon
                          icon={faEllipsisH}
                          className="cursor-pointer bg-slides-label rounded-sm w-6 h-4  absolute top-4 right-2 text-white z-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            return handleSlideMenu(index);
                          }}
                        />
                        {openTemplateMenuIndex === index && (
                          <div
                            className="h-full w-full z-50 overflow-auto"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSlideMenu();
                            }}
                          >
                            <TemplateDropdownMenu
                              template={template}
                              handleClose={handleSlideMenu}
                              isUserTemplate={isMyTemplateSelection}
                              className="left-[50%] top-[0%] w-[188px] h-auto shadow z-50 "
                              isFavorite={template.isFavorite}
                              isTemplatePopover
                            />
                          </div>
                        )}
                        <img
                          src={template.slideSnapshots[0]}
                          alt={`${index}`}
                          className={`relative mt-2 ${
                            template.layout === TEMPLATE_LAYOUT.VERTICAL
                              ? "h-full max-w-[117px] aspect-[117/180]"
                              : "w-full h-full aspect-[275/160]"
                          } inline-block hover:border-solid hover:drop-shadow rounded-lg bg-white z-10`}
                        />
                        {template?.isFavourite && (
                          <img
                            className="rounded-full bg-gradient w-[18px] h-[18px] p-1 absolute top-2 left-2"
                            src="/tool/dashboard/static/images/icons/tools/like.svg"
                            alt="like"
                          />
                        )}
                      </div>
                      <p className="text-xs mt-1">{template.name}</p>
                    </div>
                  ))}
                </InfiniteScroll>
              )}
              {loading && (
                <Loader
                  hideOverlay
                  isDark
                  className={`${
                    !filteredTemplates.length ? "h-full" : "!h-fit"
                  } !left-[50%] !absolute ${
                    !filteredTemplates.length ? "!top-[50%]" : "!top-[75%]"
                  } bottom-0 !block`}
                  loaderStyle={{
                    margin: 0,
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePopover;
